import { useState } from 'react'
import { categoryCounts, formatQuantity, pickItems } from '@kronos/domain'
import type { InventoryItem } from '../../data/inventory'
import { PageHeader } from '../primitives'
import { repository } from '../../data'
import { ItemEditor } from '../inventory/ItemEditor'
import { CategoryChips } from '../inventory/CategoryChips'
import { ItemThumb } from '../inventory/ItemArt'
import { useCan, whenText } from '../inventory/shared'

/** How many rows to draw before asking for a narrower search. */
const SHOWN = 40

/**
 * Step 1 — what arrived? A search box, the categories as chips, and the list,
 * most recently received first (`pickItems`). If it is not there, an engineer
 * can add it on the spot; anyone else is told who can.
 */
export function ReceivePick({
  items,
  onPick,
}: {
  items: readonly InventoryItem[]
  onPick: (item: InventoryItem) => void
}): JSX.Element {
  const [query, setQuery] = useState('')
  const [category, setCategory] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const can = useCan()

  const receivable = items.filter((i) => i.receivable)
  const shown = pickItems(receivable, query, category)

  return (
    <div className="page page--narrow">
      <PageHeader title="What arrived?" subtitle="Find it by name or part number — the latest deliveries are at the top." />

      <input
        type="search"
        className="inv-search"
        placeholder="Search items"
        aria-label="Search items"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        enterKeyHint="search"
      />
      <CategoryChips counts={categoryCounts(receivable)} value={category} onChange={setCategory} />

      {shown.length > 0 ? (
        <div className="panel">
          <div className="row-list">
            {shown.slice(0, SHOWN).map((item) => (
              <button key={item.id} type="button" className="row-item inv-pick" onClick={() => onPick(item)}>
                <ItemThumb imageId={item.imageId} category={item.category} name={item.name} size={48} />
                <span className="row-item__main inv-pick__main">
                  <span className="row-item__title">{item.name}</span>
                  <span className="row-item__meta">
                    {[item.category, item.partNumber, item.lastReceivedAt ? `last in ${whenText(item.lastReceivedAt)}` : null]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>
                <span className="row-item__side inv-shelf">
                  <span className={item.onShelf === 0 ? 'muted' : 'figure'}>{formatQuantity(item.onShelf, item.unit)}</span>
                  <span>on shelf</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <p className="muted inv-quiet">
          {query ? `Nothing matches “${query}”.` : 'No items in this category yet.'}
        </p>
      )}
      {shown.length > SHOWN ? (
        <p className="muted inv-quiet">Showing {SHOWN} of {shown.length} — type to narrow it down.</p>
      ) : null}

      <div className="inv-not-listed">
        {can('edit_catalog') ? (
          <>
            <span className="muted">Not in the list?</span>
            <button type="button" onClick={() => setAdding(true)}>
              Add new item
            </button>
          </>
        ) : (
          <span className="muted">
            Not in the list? Ask an engineer to add it — it takes them a minute — then receive it here. Keep the
            packing slip until then.
          </span>
        )}
      </div>

      {adding ? (
        <ItemEditor
          initialName={query}
          onClose={() => setAdding(false)}
          onSaved={(id) => {
            setAdding(false)
            // Straight on to receiving it. Something built here cannot be
            // received, so it is simply added and the list stays.
            void repository.item(id).then((item) => {
              if (item.receivable) onPick(item)
            })
          }}
        />
      ) : null}
    </div>
  )
}
